import type { MCP_ERROR_CODES } from './mcp';
import type { PROFILE_ERROR_CODES } from './user';

export const AUTH_ERROR_CODES = {
  INCORRECT_EMAIL_OR_PASSWORD: 'INCORRECT_EMAIL_OR_PASSWORD',
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
  EMAIL_ALREADY_IN_USE: 'EMAIL_ALREADY_IN_USE',
  EMAIL_NOT_FOUND: 'EMAIL_NOT_FOUND',
  INCORRECT_RESET_CODE: 'INCORRECT_RESET_CODE',
  INCORRECT_CONFIRM_CODE: 'INCORRECT_CONFIRM_CODE',
} as const;

export type AUTH_ERROR_CODES = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

export const INVITATION_ERROR_CODES = {
  EXPIRED: 'INVITATION_EXPIRED',
  ALREADY_ACCEPTED: 'INVITATION_ALREADY_ACCEPTED',
  WRONG_ACCOUNT: 'INVITATION_WRONG_ACCOUNT',
} as const;

export type INVITATION_ERROR_CODES =
  (typeof INVITATION_ERROR_CODES)[keyof typeof INVITATION_ERROR_CODES];

export const STUDENT_ERROR_CODES = {
  NUMBERS_EXISTS: 'NUMBERS_EXISTS',
  DUPLICATE_PHONE: 'DUPLICATE_PHONE',
} as const;

export type STUDENT_ERROR_CODES = (typeof STUDENT_ERROR_CODES)[keyof typeof STUDENT_ERROR_CODES];

export const STREAM_ERROR_CODES = {
  STUDENT_ALREADY_ATTACHED: 'STUDENT_ALREADY_EXIST_IN_ANOTHER_STREAM',
} as const;

export type STREAM_ERROR_CODES = (typeof STREAM_ERROR_CODES)[keyof typeof STREAM_ERROR_CODES];

export const GROUP_ERROR_CODES = {
  STUDENT_ALREADY_ATTACHED: 'STUDENT_ALREADY_EXIST_IN_ANOTHER_GROUP',
} as const;

export type GROUP_ERROR_CODES = (typeof GROUP_ERROR_CODES)[keyof typeof GROUP_ERROR_CODES];

export const TARIFF_LIMIT_ERROR_CODES = {
  LIMIT_EXCEEDED: 'TARIFF_LIMIT_EXCEEDED',
} as const;

export type TARIFF_LIMIT_ERROR_CODES =
  (typeof TARIFF_LIMIT_ERROR_CODES)[keyof typeof TARIFF_LIMIT_ERROR_CODES];

export const SUBSCRIPTION_ERROR_CODES = {
  SUBSCRIPTION_BLOCKED: 'SUBSCRIPTION_BLOCKED',
} as const;

export type SUBSCRIPTION_ERROR_CODES =
  (typeof SUBSCRIPTION_ERROR_CODES)[keyof typeof SUBSCRIPTION_ERROR_CODES];

export const ROLE_ERROR_CODES = {
  ROLE_HAS_MEMBERS: 'ROLE_HAS_MEMBERS',
} as const;

export type ROLE_ERROR_CODES = (typeof ROLE_ERROR_CODES)[keyof typeof ROLE_ERROR_CODES];

export const CHAT_ERROR_CODES = {
  CHAT_ALREADY_EXISTS: 'CHAT_ALREADY_EXISTS_FOR_SOURCE',
} as const;

export type CHAT_ERROR_CODES = (typeof CHAT_ERROR_CODES)[keyof typeof CHAT_ERROR_CODES];

export const AI_ERROR_CODES = {
  CONTEXT_LENGTH_EXCEEDED: 'CONTEXT_LENGTH_EXCEEDED',
  AI_FIX_IN_PROGRESS: 'AI_FIX_IN_PROGRESS',
} as const;

export type AI_ERROR_CODES = (typeof AI_ERROR_CODES)[keyof typeof AI_ERROR_CODES];

export const LIVE_SESSION_ERROR_CODES = {
  /** The chosen account is already running another session. */
  ACCOUNT_BUSY: 'LIVE_ACCOUNT_BUSY',
  /** The provider rejected the account's token — it must be reconnected. */
  ACCOUNT_DISCONNECTED: 'LIVE_ACCOUNT_DISCONNECTED',
  /** The session is over, so there is nothing to join. */
  SESSION_ENDED: 'LIVE_SESSION_ENDED',
} as const;

export type LIVE_SESSION_ERROR_CODES =
  (typeof LIVE_SESSION_ERROR_CODES)[keyof typeof LIVE_SESSION_ERROR_CODES];

export const ORG_STRUCTURE_ERROR_CODES = {
  /** The unit still has people or child units and no destination was given. */
  UNIT_NOT_EMPTY: 'UNIT_NOT_EMPTY',
  /** A sibling unit already has this name. */
  UNIT_NAME_TAKEN: 'UNIT_NAME_TAKEN',
  /** A unit cannot be moved under itself or one of its descendants. */
  UNIT_CYCLE: 'UNIT_CYCLE',
  /** The position is held by employees and no replacement was given. */
  POSITION_IN_USE: 'POSITION_IN_USE',
  POSITION_NAME_TAKEN: 'POSITION_NAME_TAKEN',
} as const;

export type ORG_STRUCTURE_ERROR_CODES =
  (typeof ORG_STRUCTURE_ERROR_CODES)[keyof typeof ORG_STRUCTURE_ERROR_CODES];

export const EMPLOYEE_ERROR_CODES = {
  /** Phone numbers are unique per platform. */
  PHONE_TAKEN: 'PHONE_TAKEN',
  PERSONNEL_NUMBER_TAKEN: 'PERSONNEL_NUMBER_TAKEN',
} as const;

export type EMPLOYEE_ERROR_CODES = (typeof EMPLOYEE_ERROR_CODES)[keyof typeof EMPLOYEE_ERROR_CODES];

export const ASSIGNMENT_ERROR_CODES = {
  /** The audience resolves to nobody. */
  AUDIENCE_EMPTY: 'ASSIGNMENT_AUDIENCE_EMPTY',
  /** The course is not published, so it cannot be assigned. */
  COURSE_NOT_ACTIVE: 'ASSIGNMENT_COURSE_NOT_ACTIVE',
  /** Only an active assignment can be changed or cancelled. */
  NOT_ACTIVE: 'ASSIGNMENT_NOT_ACTIVE',
  /** The course of an assignment cannot be replaced. */
  TARGET_LOCKED: 'ASSIGNMENT_TARGET_LOCKED',
} as const;

export type ASSIGNMENT_ERROR_CODES =
  (typeof ASSIGNMENT_ERROR_CODES)[keyof typeof ASSIGNMENT_ERROR_CODES];

export type ERROR_CODES =
  | AUTH_ERROR_CODES
  | INVITATION_ERROR_CODES
  | STUDENT_ERROR_CODES
  | STREAM_ERROR_CODES
  | GROUP_ERROR_CODES
  | TARIFF_LIMIT_ERROR_CODES
  | SUBSCRIPTION_ERROR_CODES
  | ROLE_ERROR_CODES
  | CHAT_ERROR_CODES
  | AI_ERROR_CODES
  | LIVE_SESSION_ERROR_CODES
  | ORG_STRUCTURE_ERROR_CODES
  | EMPLOYEE_ERROR_CODES
  | ASSIGNMENT_ERROR_CODES
  | MCP_ERROR_CODES
  | PROFILE_ERROR_CODES;

export type ErrorResponse = {
  message: string;
  code: ERROR_CODES;
  data?: unknown;
};

export type SuccessResponse<T> = T;

export type CommonResponse<T> = SuccessResponse<T> | ErrorResponse;

export type SortOrder = 'asc' | 'desc';

export type DefaultGetParams<SortBy> = {
  page: number;
  limit: number;
  sort_by?: SortBy;
  sort?: SortOrder;
  search?: string;
};
